import { useState } from 'react';
import { useNavigate } from 'react-router';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useCopyOrderToCart } from '~/api';
import { extractGraphQLErrorMessages } from '~/utils';
import type { SkippedOrderItem } from '~/graphql/graphql';

export function useCopyOrderToCartAction() {
  const { t } = useTranslation('order');
  const navigate = useNavigate();
  const { mutate } = useCopyOrderToCart();
  const [skippedItems, setSkippedItems] = useState<SkippedOrderItem[] | null>(
    null
  );
  const [hasCart, setHasCart] = useState(false);
  const [pendingOrderIds, setPendingOrderIds] = useState<Set<string>>(
    new Set()
  );

  const clearPending = (orderId: string) => {
    setPendingOrderIds((prev) => {
      const next = new Set(prev);
      next.delete(orderId);
      return next;
    });
  };

  const copyOrderToCart = (orderId: string) => {
    setPendingOrderIds((prev) => new Set(prev).add(orderId));
    mutate(
      { orderId },
      {
        onSuccess: (data) => {
          clearPending(orderId);
          const result = data.copyOrderToCart;
          if (result.cart != null && result.skippedItems.length === 0) {
            toast.success(t('order.copy_to_cart_success'));
            navigate('/cart');
            return;
          }
          setHasCart(result.cart != null);
          setSkippedItems(result.skippedItems);
        },
        onError: (error) => {
          clearPending(orderId);
          toast.error(
            t('order.copy_to_cart_error', {
              message: extractGraphQLErrorMessages(error).join(', '),
            })
          );
        },
      }
    );
  };

  const closeSkippedItemsModal = () => {
    const shouldNavigate = hasCart;
    setSkippedItems(null);
    setHasCart(false);
    if (shouldNavigate) {
      navigate('/cart');
    }
  };

  return {
    copyOrderToCart,
    pendingOrderIds,
    skippedItems,
    closeSkippedItemsModal,
  };
}
